

import { storage } from './storage';
import { Plant, Reminders } from '../types';

class NotificationService {
  private isSupported: boolean = 'Notification' in window;

  async requestPermission(): Promise<boolean> {
    if (!this.isSupported) return false;
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  get hasPermission(): boolean {
    return this.isSupported && Notification.permission === 'granted';
  }

  async sendLocalNotification(title: string, body: string, icon?: string) {
    if (!this.hasPermission) return;

    const registration = await navigator.serviceWorker.ready;
    if (registration && 'showNotification' in registration) {
      // Fix: Use 'as any' to allow properties like 'vibrate' and 'badge' which might be missing in some NotificationOptions definitions.
      registration.showNotification(title, {
        body,
        icon: icon || 'https://dvncpslpjsggfknqxuga.supabase.co/storage/v1/object/public/Arquivos/LOGO.png',
        badge: 'https://dvncpslpjsggfknqxuga.supabase.co/storage/v1/object/public/Arquivos/LOGO.png',
        vibrate: [200, 100, 200],
        tag: 'raizama-reminder'
      } as any);
    } else {
      new Notification(title, { body, icon });
    }
  }

  /**
   * Varre a coleção do usuário e dispara notificações para lembretes ativos.
   * Evita duplicidade usando localStorage para marcar notificações já enviadas no dia.
   */
  async checkAndNotifyReminders() {
    if (!this.hasPermission) return;

    // Cleanup old keys from previous days
    const today = new Date().toDateString();
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('raizama_notified_') && key !== `raizama_notified_${today}`) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));

    const plants = await storage.getPlants();
    const notifiedKey = `raizama_notified_${today}`;
    const alreadyNotified = JSON.parse(localStorage.getItem(notifiedKey) || '[]');

    for (const plant of plants) {
      if (alreadyNotified.includes(plant.id)) continue;

      const activeReminders = this.getActiveReminders(plant);
      if (activeReminders.length > 0) {
        const title = `🌿 Lembrete Raizama: ${plant.nomeComum}`;
        const body = `Você tem tarefas pendentes: ${activeReminders.join(', ')}.`;
        
        await this.sendLocalNotification(title, body, plant.imagens?.[0]);
        alreadyNotified.push(plant.id);
      }
    }

    localStorage.setItem(notifiedKey, JSON.stringify(alreadyNotified));
  }

  private estimateNextDate(lastDateStr: string, freqText: string): Date | null {
    const last = new Date(lastDateStr);
    if (isNaN(last.getTime())) return null;

    let daysToAdd = 7; 
    const text = freqText.toLowerCase();

    if (text.includes('dia') || text.includes('diária') || text.includes('diaria')) {
      const match = text.match(/(\d+)/);
      if (match && !(text.includes('vez') && text.includes('dia'))) {
         daysToAdd = parseInt(match[1], 10);
      } else {
         daysToAdd = 1;
      }
    } else if (text.includes('semana')) {
      const match = text.match(/(\d+)/);
      if (match) {
         const freq = parseInt(match[1], 10);
         daysToAdd = Math.ceil(7 / (freq > 0 ? freq : 1));
      } else {
         daysToAdd = 7;
      }
    } else if (text.includes('mês') || text.includes('mes')) {
       const match = text.match(/(\d+)/);
       if (match) {
         daysToAdd = 30 * parseInt(match[1], 10);
       } else {
         daysToAdd = 30;
       }
    }

    const next = new Date(last);
    next.setDate(next.getDate() + daysToAdd);
    next.setHours(0, 0, 0, 0); // reset time to start of day
    return next;
  }

  private getActiveReminders(plant: Plant): string[] {
    const list: string[] = [];
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (plant.reminders?.watering) {
      if (plant.ultimaRega && plant.planoManejo?.rega) {
        const nextWatering = this.estimateNextDate(plant.ultimaRega, plant.planoManejo.rega);
        if (nextWatering && nextWatering.getTime() <= now.getTime()) {
           list.push('Rega');
        }
      } else {
        list.push('Rega (defina a última rega e a frequência)');
      }
    }
    
    // Simplificando os outros lembretes por enquanto, 
    // mas poderiam usar a mesma lógica se tivessem campos de "última vez"
    if (plant.reminders?.fertilizing) list.push('Adubação');
    if (plant.reminders?.pruning) list.push('Poda');
    if (plant.reminders?.repotting) list.push('Reenvasar');
    return list;
  }

  async testNotification() {
    await this.sendLocalNotification(
      "🌱 Teste de Notificação",
      "As notificações do Raizama Garden CARE estão funcionando perfeitamente!"
    );
  }
}

export const notifications = new NotificationService();
